import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "@/components/ui/resizable";
import { useNavigate, useParams } from "@tanstack/react-router";
import { Pencil } from "lucide-react";
import { type ElementType, Suspense } from "react";
import { useTranslation } from "react-i18next";

import { ErrorCard } from "@/components/shared/errors/error-card";
import { InvoiceViewer } from "@/components/shared/invoice-review/invoice-viewer";
import { PreFetchPDF } from "@/components/shared/pdf-tools/components/pre-fetch-pdf";
import { ToolbarButton } from "@/components/shared/toolbars/toolbar-button";
import { LoadingSpinner } from "@/components/ui/spinner";
import InvoiceProvider from "@/features/invoice-standard-review/accounting/ReviewProvider";
import { useLoadAndSyncInvoicePdf } from "@/hooks/use-load-and-sync-invoice-pdf";

type Props = {
  invoiceId: string;
  invoiceViewerPanel: {
    maxSize: number;
    defaultSize: number;
  };
  invoiceReviewPanel: {
    defaultSize: number;
    maxSize: number;
    component: ElementType<{ invoiceId: string }>;
  };
};

function InvoiceViewerExtraToolbarButtons({
  invoiceId,
  companySlug,
  isDigitalInvoice,
}: {
  invoiceId?: string;
  companySlug?: string;
  isDigitalInvoice?: boolean;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleEditPages = () => {
    if (!invoiceId || !companySlug) {
      return;
    }

    navigate({
      to: "/$company/buchhaltung/rechnung/$id/seiten-bearbeiten",
      params: { company: companySlug, id: invoiceId },
    });
  };

  return (
    <ToolbarButton
      onClick={handleEditPages}
      tooltip={{
        title: t("pdf_edit_pages_title"),
        description: t("pdf_edit_pages_description"),
      }}
      disabledTooltip={{
        title: t("pdf_edit_pages_disabled_title"),
        description: t("pdf_edit_pages_disabled_description"),
      }}
      disabled={isDigitalInvoice}
      className="px-2 py-0 text-xs border border-gray-300 bg-gray-100 font-medium flex items-center h-8 text-gray-800 hover:bg-gray-200 hover:border-gray-400"
    >
      <Pencil width={14} className="mr-1" />
      {t("pdf_edit_pages")}
    </ToolbarButton>
  );
}

export const InvoiceReviewPanel = ({
  invoiceId,
  invoiceViewerPanel,
  invoiceReviewPanel,
}: Props) => {
  const {
    coordinates,
    setCoordinates,
    setErrorMessage,
    errorMessage,
    setPdfBlob,
    pdfBlob,
  } = useLoadAndSyncInvoicePdf();

  if (errorMessage) {
    return (
      <div className="flex h-full items-center justify-center">
        <ErrorCard errorMessage={errorMessage} title="Achtung" />
      </div>
    );
  }

  return (
    <InvoiceProvider invoiceId={invoiceId} setCoordinates={setCoordinates}>
      <div id="invoice-review-screen" className="h-screen">
        <PreFetchPDF
          invoiceId={invoiceId}
          setPdfBlob={setPdfBlob}
          setErrorMessage={setErrorMessage}
        />
        <Suspense fallback={<LoadingSpinner />}>
          <ResizablePanelGroup
            direction="horizontal"
            autoSaveId="invoice-review-company-panel-persistence"
            className="h-screen"
          >
            <ResizablePanel
              minSize={25}
              maxSize={invoiceViewerPanel.maxSize}
              defaultSize={invoiceViewerPanel.defaultSize}
            >
              <div className="h-full bg-gray-200">
                <InvoiceViewer
                  pdfBlob={pdfBlob ?? ""}
                  invoiceId={invoiceId}
                  coordinates={coordinates}
                  canvasEnabled={false}
                  ExtraToolBarButtons={InvoiceViewerExtraToolbarButtons}
                />
              </div>
            </ResizablePanel>
            <ResizableHandle withHandle />
            <ResizablePanel
              minSize={35}
              maxSize={invoiceReviewPanel.maxSize}
              defaultSize={invoiceReviewPanel.defaultSize}
            >
              <div className="h-full">
                <invoiceReviewPanel.component invoiceId={invoiceId} />
              </div>
            </ResizablePanel>
          </ResizablePanelGroup>
        </Suspense>
      </div>
    </InvoiceProvider>
  );
};
